import { useState } from "react";
import { Flag } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { reportContent } from "@/lib/moderation-community.functions";

const REASONS = [
  { value: "spam", label: "Spam or scam" },
  { value: "harassment", label: "Harassment or bullying" },
  { value: "hate", label: "Hate speech" },
  { value: "sexual", label: "Sexual content" },
  { value: "violence", label: "Violence or threats" },
  { value: "self_harm", label: "Self-harm" },
  { value: "misinfo", label: "Misinformation" },
  { value: "other", label: "Something else" },
] as const;

type Kind = "song_comment" | "content_comment" | "circle_post" | "wisdom_post" | "profile";

export function ReportButton({
  targetKind,
  targetId,
  variant = "ghost",
  label,
}: {
  targetKind: Kind;
  targetId: string;
  variant?: "ghost" | "outline" | "secondary";
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<string>("spam");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const report = useServerFn(reportContent);

  async function submit() {
    setSubmitting(true);
    try {
      const res = await report({ data: { targetKind, targetId, reason: reason as any, notes: notes || undefined } });
      toast.success((res as any)?.duplicate ? "Already reported — thank you" : "Report submitted");
      setOpen(false);
      setNotes("");
    } catch (e: any) {
      toast.error(e?.message ?? "Could not submit report");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={variant} size="sm" className="gap-1 h-8 min-h-[44px] md:min-h-0">
          <Flag className="w-4 h-4" aria-hidden />
          <span className={label ? "" : "sr-only"}>{label ?? "Report"}</span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Report content</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-sm text-muted-foreground">Reason</label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Details (optional)</label>
            <Textarea
              maxLength={1000}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Share any context that would help our moderators."
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting}>{submitting ? "Sending…" : "Submit report"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
